import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  Box,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import { EasyCheckButton } from '../../common/Controls';
import { 
  Visibility, 
  VisibilityOff, 
  Lock,
  CheckCircle
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { easyCheckAuthService } from '../../../services/auth/authService';
import { useEasyCheck } from '../../../context/EasyCheckContext';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
  },
}));

const ChangePasswordForm = ({ username, onSuccess }) => {
  const theme = useTheme();
  const { addNotification } = useEasyCheck();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset
  } = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');
      
      const result = await easyCheckAuthService.changePassword({
        username,
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      });
      
      if (result.success) {
        setSuccess(true);
        addNotification('Password changed successfully!', 'success');
        reset();
        if (onSuccess) onSuccess();
      } else {
        setError(result.message || 'Failed to change password. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Password Changed Successfully!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your password has been updated. Please use your new password for future logins.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          Change Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your current password and choose a new secure password.
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Controller
              name="currentPassword"
              control={control}
              rules={{ required: 'Current password is required' }}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  label="Current Password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  fullWidth
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          edge="end"
                        >
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />

            <Controller
              name="newPassword"
              control={control}
              rules={{ 
                required: 'New password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters'
                }
              }}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  fullWidth
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              rules={{ 
                required: 'Please confirm your new password',
                validate: value => value === newPassword || 'Passwords do not match'
              }}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  label="Confirm New Password"
                  type="password"
                  fullWidth
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />

            <EasyCheckButton
              buttonType="auth"
              type="submit"
              variant="contained"
              fullWidth
              loading={loading}
              size="large"
              sx={{ mt: 2 }}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </EasyCheckButton>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChangePasswordForm; 